import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Calendar.css';

// À adapter selon où tu déclares isInSeason
function isInSeason(date) {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0 = janvier, 5 = juin
  const day = date.getDate();
  const dayOfWeek = date.getDay(); // 0 = dimanche, 3 = mercredi, 6 = samedi

  // Juillet et août : tous les jours ouverts
  if (month === 6 || month === 7) {
    return true;
  }

  // Juin
  if (month === 5) {
    if (day < 14) {
      // Avant le 14 juin : samedi (6) et dimanche (0)
      return dayOfWeek === 6 || dayOfWeek === 0;
    } else {
      // À partir du 14 juin : mercredi (3), samedi (6), dimanche (0)
      return dayOfWeek === 3 || dayOfWeek === 6 || dayOfWeek === 0;
    }
  }

  // Autres mois : adapte selon ta règle (exemple : samedi/dimanche)
  return dayOfWeek === 6 || dayOfWeek === 0;
}

function Calendar({ currentDate, sessions, onDateChange, getAvailableHours, participants = [], isAdmin = false }) {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());
  
  // Fonction pour générer le calendrier du mois
  const generateCalendar = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    const calendar = [];
    const totalDays = 42; // 6 semaines pour couvrir toutes les possibilités
    
    for (let i = 0; i < totalDays; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      // Vérifier si la date est dans le mois actuel
      const isCurrentMonth = currentDate.getMonth() === currentMonth;
      
      // Vérifier si la date est dans la saison
      const inSeason = isInSeason(currentDate);
      
      // Trouver les sessions pour cette date
      const dateStr = currentDate.toISOString().split('T')[0];
      const dateSessions = sessions.filter(session => session.date === dateStr);
      
      calendar.push({
        date: new Date(currentDate),
        isCurrentMonth,
        inSeason,
        sessions: dateSessions
      });
    }
    
    return calendar;
  };
  
  // Fonction pour changer de mois
  const changeMonth = (delta) => {
    let newMonth = currentMonth + delta;
    let newYear = currentYear;
    
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };
  
  // Fonction pour sélectionner une date
  const selectDate = (date) => {
    onDateChange(date);
    // Format YYYY-MM-DD en local
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayNum = String(date.getDate()).padStart(2, '0');
    const localDateStr = `${year}-${month}-${dayNum}`;
    navigate(`/daily/${localDateStr}`);
  };
  
  const calendar = generateCalendar();
  
  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button
          className="action-button"
          onClick={() => changeMonth(-1)}
          style={{ marginRight: 8 }}
        >
          &laquo; Mois précédent
        </button>
        <h2 className="funky-title">
          {new Date(currentYear, currentMonth).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
        </h2>
        <button
          className="action-button"
          onClick={() => changeMonth(1)}
          style={{ marginLeft: 8 }}
        >
          Mois suivant &raquo;
        </button>
      </div>
      
      <div className="calendar-grid">
        <div className="calendar-days">
          {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
        </div>
        <div className="calendar-dates">
          {calendar.map((day, index) => {
            const validHours = getAvailableHours(day.date);
            const validSessions = day.sessions.filter(s => validHours.includes(s.time));
            const blockedSessions = validSessions.filter(s => s.isBlocked);
            const allSessionsBlocked = validSessions.length > 0 && blockedSessions.length === validSessions.length;

            let dayStatus = '';
            let dayIndicator = '';

            if (!day.inSeason) {
              dayStatus = 'off-season';
              dayIndicator = '';
            } else if (allSessionsBlocked) {
              dayStatus = 'has-blocked-sessions';
              dayIndicator = '🔒';
            } else if (validSessions.length > 0) {
              // Tous les jours avec des sessions ouvertes sont VERTS
              dayStatus = 'available-day';
              dayIndicator = '✅';
            } else {
              dayStatus = 'no-sessions';
              dayIndicator = '';
            }

            const isClickable = !allSessionsBlocked || isAdmin;

            return (
              <div
                key={index}
                onClick={() => isClickable && selectDate(day.date)}
                className={`calendar-date ${
                  day.isCurrentMonth ? 'current-month' : 'other-month'
                } ${
                  day.date.toDateString() === currentDate.toDateString() ? 'selected-date' : ''
                } ${
                  day.inSeason ? 'in-season' : 'off-season'
                } clickable ${dayStatus} ${allSessionsBlocked ? 'blocked' : ''}`}
                style={allSessionsBlocked && !isAdmin ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
              >
                <div className="date-number">
                  {day.date.getDate()}
                  {dayIndicator && <span className="day-indicator">{dayIndicator}</span>}
                </div>
                {day.inSeason && validSessions.length > 0 && (
                  <div className="session-info">
                    <div className="session-count">
                      {Array.from(new Set(validSessions.map(s => s.time))).length} session(s)
                    </div>
                    {/* Le nombre d'inscrits n'est plus affiché */}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Légende simplifiée */}
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-indicator available"></div>
          <span>✅ <strong>Sessions ouvertes</strong></span>
        </div>
        <div className="legend-item">
          <div className="legend-indicator off-season"></div>
          <span>⚪ <strong>Hors saison</strong></span>
        </div>
      </div>
    </div>
  );
}

function getAvailableHours(date) {
  const month = date.getMonth(); // 0 = janvier, 5 = juin, 6 = juillet, 7 = août
  const day = date.getDate();
  const dayOfWeek = date.getDay(); // 0 = dimanche, 3 = mercredi, 6 = samedi

  // Horaires standards (base)
  const hours = [
    '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00'
  ];

  // Juillet et août : tous les jours ouverts avec horaires étendus
  if (month === 6 || month === 7) {
    return [
      ...hours,
      '17:30', '18:00', '18:30', '19:00', '19:30' // ← NOUVEAUX CRÉNEAUX
    ];
  }

  // Juin
  if (month === 5) {
    if (day < 14) {
      // Avant le 14 juin : samedi (6) et dimanche (0)
      if (dayOfWeek === 6 || dayOfWeek === 0) return hours;
      return [];
    } else {
      // À partir du 14 juin : mercredi (3), samedi (6), dimanche (0) avec horaires étendus
      if (dayOfWeek === 3 || dayOfWeek === 6 || dayOfWeek === 0) {
        return [
          ...hours,
          '17:30', '18:00', '18:30', '19:00', '19:30' // ← NOUVEAUX CRÉNEAUX
        ];
      }
      return [];
    }
  }

  // Autres mois : samedi et dimanche uniquement (horaires standards)
  if (dayOfWeek === 6 || dayOfWeek === 0) return hours;
  return [];
}

export default Calendar;
